import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../../components/shared/PageHeader';
import DataToolbar, { StatusPill, Card } from '../../components/shared/DataToolbar';
import { LoadingState, ErrorState, EmptyResults } from '../../components/shared/Pending';
import { CalendarDays, MoreHorizontal, FileText } from 'lucide-react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '../../components/ui/dropdown-menu';
import { useApi } from '../../api/useApi';
import { listBookings } from '../../api/hostApi';

const rowsOf = (value) => Array.isArray(value) ? value : (value?.items || value?.data || []);
const coverUrl = (booking) => booking?.property?.cover_image?.url || booking?.property?.cover_image || '';
const guestName = (booking) => booking?.guest?.name || booking?.customer?.name || '—';
const paymentStatus = (booking) => booking?.transaction?.status || (booking?.paid_at ? 'paid' : booking?.status === 'pending_payment' ? 'pending' : '—');
const money = (value, currency = 'USD') => value == null || value === '' ? '—' : new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(Number(value));

export default function HostBookings() {
  const { data, loading, error, refetch } = useApi(listBookings, []);
  const rows = useMemo(() => rowsOf(data), [data]);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');

  const filtered = rows.filter((booking) => {
    const hay = `${guestName(booking)} ${booking.property?.title || ''} ${booking.reference || booking.id}`.toLowerCase();
    return (!q || hay.includes(q.toLowerCase())) && (!status || status === 'All' || (booking.status || '').toLowerCase() === status.toLowerCase());
  });

  return (
    <div>
      <PageHeader title="Bookings" description="Track reservations, guests and payment status across your properties." />
      <DataToolbar
        search={q}
        onSearch={setQ}
        placeholder="Search by guest, property or booking ID…"
        filters={[{ label: 'Status', value: status, options: ['All', 'Pending', 'Confirmed', 'Completed', 'Cancelled'], onChange: setStatus }]}
      />

      {loading ? <LoadingState label="Loading bookings…" /> : error ? <ErrorState error={error} onRetry={refetch} />
        : filtered.length === 0 ? <EmptyResults icon={CalendarDays} title="No bookings" description="Bookings matching the current filters will appear here." />
        : (
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium">Booking</th>
                    <th className="px-4 py-3 text-left font-medium">Property</th>
                    <th className="px-4 py-3 text-left font-medium">Primary guest</th>
                    <th className="px-4 py-3 text-left font-medium">Dates</th>
                    <th className="px-4 py-3 text-right font-medium">Total</th>
                    <th className="px-4 py-3 text-left font-medium">Booking</th>
                    <th className="px-4 py-3 text-left font-medium">Payment</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((booking) => (
                    <tr key={booking.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="font-mono text-xs font-medium text-slate-700">{booking.reference || `#${booking.id}`}</div>
                        <div className="mt-0.5 text-[11px] text-slate-400">{booking.created_at ? new Date(booking.created_at).toLocaleDateString() : ''}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex min-w-0 items-center gap-2">
                          {coverUrl(booking) ? <img src={coverUrl(booking)} className="h-9 w-9 rounded object-cover" alt="" /> : <div className="h-9 w-9 rounded bg-slate-100" />}
                          <span className="max-w-[220px] truncate font-medium text-slate-900">{booking.property?.title || '—'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-slate-900">{guestName(booking)}</div>
                        <div className="mt-0.5 text-xs text-slate-500">{booking.guest?.email || booking.customer?.email || ''}</div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-slate-600">{booking.check_in || '—'} → {booking.check_out || '—'} <span className="text-xs text-slate-400">({booking.nights || 0}n)</span></td>
                      <td className="px-4 py-3 text-right font-semibold tabular-nums">{money(booking.total_amount, booking.currency)}</td>
                      <td className="px-4 py-3"><StatusPill status={booking.status} /></td>
                      <td className="px-4 py-3"><StatusPill status={paymentStatus(booking)} /></td>
                      <td className="px-4 py-3">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild><button className="grid h-8 w-8 place-items-center rounded-lg hover:bg-slate-100"><MoreHorizontal className="h-4 w-4" /></button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem asChild><Link to={`/host/bookings/${booking.id}`}><FileText className="mr-2 h-4 w-4" /> View details</Link></DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
    </div>
  );
}
